import React, { useEffect, useState } from "react";
import {
  Box,
  Checkbox,
  Container,
  FormControl,
  Grid,
  ListItemText,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import { useFormikContext } from "formik";
import CssTextField from "Components/CssTextField";
import CssButton from "Components/CssButton";
import CssCircularProgress from "Components/CssCircularProgress";
import CssSelectField from "Components/CssSelectField";
import AccordionContainer from "Components/Accordion";
import CustomSnackbar from "Components/CustomSnackbar";
import useRequests from "services/request-hook";
import useApi from "services/api-hook";
import FileUpload from "Components/FileUpload";
import {
  HEADER_ICON_CATEGORIES,
  HEADER_ICON_ERROR,
  HEADER_ICON_KEY,
  THEME_SCREEN_SECTIONS,
  buildThemeConfigurationPayload,
  getColorFieldLabel,
  isValidHeaderIcon,
} from "./themeConfiguration";

const HEADER_ICON_ACCEPT = { "image/*": [".svg", ".png", ".webp"] };
const noop = () => {};

const COLOR_PICKER_FALLBACK = "#000000";
const purpleLight = "#6d4e9c";

const MENU_PROPS = {
  PaperProps: {
    style: {
      maxHeight: 280,
    },
  },
};

const ThemeConfigurationForm = ({
  edit,
  isSaving,
  isPublished,
  onSave,
  categoryId,
  categoryKey,
  currentSimulationId,
}) => {
  const { values, setFieldValue } = useFormikContext();
  const themeConfiguration = values.theme_configuration || {};
  const resolvedCategoryId = categoryId || values?.category || "";
  const showHeaderIcon = HEADER_ICON_CATEGORIES.includes(
    String(categoryKey || "").toLowerCase()
  );
  const headerIconRef = React.useRef(null);
  const [isIconUploading, setIsIconUploading] = useState(false);
  // FileUpload uploads whatever selectedFile points at: the dropped File, else
  // the icon saved on the scenario until the user removes it.
  const [savedIconUrl] = useState(themeConfiguration[HEADER_ICON_KEY]?.url || "");
  const [iconRemoved, setIconRemoved] = useState(false);
  const headerIconSelected =
    values.header_icon_file || (iconRemoved ? "" : savedIconUrl);

  const { getScenarioListApi, editScenarioApi } = useRequests();
  const getScenarioList = useApi(getScenarioListApi);

  const [categorySimulations, setCategorySimulations] = useState([]);
  const [selectedSimulationIds, setSelectedSimulationIds] = useState([]);
  const [isSimulationListLoading, setIsSimulationListLoading] = useState(false);
  const [isCopyingConfiguration, setIsCopyingConfiguration] = useState(false);
  const [simulationListError, setSimulationListError] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  useEffect(() => {
    let isMounted = true;

    setSelectedSimulationIds([]);
    setCategorySimulations([]);
    setSimulationListError("");

    if (!resolvedCategoryId) {
      setIsSimulationListLoading(false);
      return () => {
        isMounted = false;
      };
    }

    setIsSimulationListLoading(true);

    getScenarioList
      .request(resolvedCategoryId, 1, "ALL", "")
      .then((res) => {
        if (!isMounted) return;

        const rawList = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.data?.data)
            ? res.data.data
            : [];

        const seenIds = new Set();
        const validSimulations = rawList.filter((simulation) => {
          if (!simulation?._id || !simulation?.title) return false;
          if (currentSimulationId && simulation._id === currentSimulationId) {
            return false;
          }
          if (seenIds.has(simulation._id)) return false;
          seenIds.add(simulation._id);
          return true;
        });

        setCategorySimulations(validSimulations);
        setSimulationListError("");
      })
      .catch(() => {
        if (!isMounted) return;
        setCategorySimulations([]);
        setSimulationListError(
          "Unable to load simulations. Please try again."
        );
      })
      .finally(() => {
        if (isMounted) {
          setIsSimulationListLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch only when category/current simulation changes
  }, [resolvedCategoryId, currentSimulationId]);

  const handleColorChange = (screenKey, fieldKey, value) => {
    setFieldValue(`theme_configuration.${screenKey}.${fieldKey}`, value);
  };

  const handleClearScreen = (screenKey) => {
    THEME_SCREEN_SECTIONS.find((section) => section.key === screenKey)
      ?.fields.forEach((fieldKey) => {
        setFieldValue(`theme_configuration.${screenKey}.${fieldKey}`, "");
      });
  };

  // FileUpload calls this with the blob upload response, a plain URL (System
  // picker) or {} when the image is removed.
  const handleHeaderIconUpload = (uploaded) => {
    let url = typeof uploaded === "string" ? uploaded : uploaded?.url;
    if (!url) {
      setIconRemoved(true);
      setFieldValue(`theme_configuration.${HEADER_ICON_KEY}`, null);
      return;
    }
    setIconRemoved(false);
    if (url.includes("?")) {
      const path = url.substring(0, url.indexOf("?")).split("/");
      url = process.env.REACT_APP_CDN_URL
        ? `${process.env.REACT_APP_CDN_URL}/${path[3]}/${path[4]}`
        : url.substring(0, url.indexOf("?"));
    }
    setFieldValue(`theme_configuration.${HEADER_ICON_KEY}`, { url });
  };

  const getSelectedTitles = (selectedIds) => {
    return selectedIds
      .map((id) => categorySimulations.find((item) => item._id === id)?.title)
      .filter(Boolean);
  };

  const handleSimulationSelectionChange = (event) => {
    const value = event.target.value;
    setSelectedSimulationIds(
      typeof value === "string" ? value.split(",") : value
    );
  };

  const handleCopyConfiguration = async () => {
    if (
      !selectedSimulationIds.length ||
      isSimulationListLoading ||
      isCopyingConfiguration
    ) {
      return;
    }

    const payload = {
      theme_configuration: buildThemeConfigurationPayload(themeConfiguration),
    };

    setIsCopyingConfiguration(true);
    try {
      const responses = await Promise.all(
        selectedSimulationIds.map((simulationId) =>
          editScenarioApi(simulationId, payload)
        )
      );

      const allSucceeded = responses.every((res) => res?.status === 200);
      if (!allSucceeded) {
        throw new Error("Copy failed for one or more simulations");
      }

      setSnackbarSeverity("success");
      setSnackbarMessage(
        "Theme configuration copied to selected simulations successfully."
      );
      setOpenSnackbar(true);
    } catch (error) {
      console.log(error);
      setSnackbarSeverity("error");
      setSnackbarMessage(
        "Unable to copy theme configuration. Please try again."
      );
      setOpenSnackbar(true);
    } finally {
      setIsCopyingConfiguration(false);
    }
  };

  const isCopyButtonDisabled =
    !selectedSimulationIds.length ||
    isSimulationListLoading ||
    isCopyingConfiguration ||
    !resolvedCategoryId;

  return (
    <Container sx={{ mt: 2, mb: 4, maxWidth: "md !important" }}>
      <Typography
        sx={{ color: "#fff", mb: 2, fontSize: "0.9rem", opacity: 0.85 }}
      >
        Configure per-screen colors for the SR player. Leave a field empty to
        use the selected theme preset default.
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {THEME_SCREEN_SECTIONS.map((section) => (
          <AccordionContainer key={section.key} title={section.title}>
            <Grid container spacing={2}>
              {section.fields.map((fieldKey) => {
                const fieldName = `theme_configuration.${section.key}.${fieldKey}`;
                const fieldValue = themeConfiguration[section.key]?.[fieldKey] || "";

                return (
                  <Grid item xs={12} sm={6} key={fieldName}>
                    <CssTextField
                      variant="outlined"
                      size="small"
                      name={fieldName}
                      id={fieldName}
                      value={fieldValue || COLOR_PICKER_FALLBACK}
                      type="color"
                      onChange={(e) =>
                        handleColorChange(section.key, fieldKey, e.target.value)
                      }
                      InputProps={{
                        className: "text-field",
                      }}
                      label={getColorFieldLabel(fieldKey)}
                      helperText={
                        fieldValue
                          ? fieldValue
                          : "Empty — uses theme preset default"
                      }
                    />
                  </Grid>
                );
              })}
              <Grid item xs={12}>
                <Typography
                  component="button"
                  type="button"
                  onClick={() => handleClearScreen(section.key)}
                  sx={{
                    color: "#a084c7",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontSize: "0.85rem",
                    p: 0,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Clear {section.title} colors
                </Typography>
              </Grid>
            </Grid>
          </AccordionContainer>
        ))}
      </Box>

      {showHeaderIcon && (
        <Box sx={{ mt: 3 }}>
          <FileUpload
            name="header_icon_file"
            id="header_icon_file"
            multiple={false}
            selectedFile={headerIconSelected}
            onFileUpload={handleHeaderIconUpload}
            handleUpdatedFile={noop}
            saveImage={noop}
            saveOnDisable={noop}
            optionalImage={false}
            simulationForm={true}
            accept={HEADER_ICON_ACCEPT}
            validateValue={isValidHeaderIcon}
            validationMessage={HEADER_ICON_ERROR}
            childFunc={headerIconRef}
            isPublished={isPublished}
            bucket={"scenario-banners"}
            title="Header Icon"
            handleSpinner={setIsIconUploading}
          />
          <Typography
            sx={{ color: "#fff", mt: 1, fontSize: "0.8rem", opacity: 0.75 }}
          >
            Small icon on the start card, question header, feedback/summary
            titles and thank-you card. Leave empty to use the theme's default
            icon.
          </Typography>
        </Box>
      )}

      <Stack alignItems="center" sx={{ mt: 3 }}>
        <CssButton
          type="button"
          variant="contained"
          onClick={() => onSave(themeConfiguration)}
          disabled={isSaving || isIconUploading || (edit && isPublished)}
          sx={{
            background: purpleLight,
            color: "#fff",
            width: "fit-content",
            minWidth: 160,
            "&:hover": { background: purpleLight },
          }}
        >
          {isSaving ? (
            <CssCircularProgress size={20} />
          ) : edit ? (
            "Update Colors"
          ) : (
            "Save"
          )}
        </CssButton>
      </Stack>

      <Box sx={{ mt: 4, width: "100%", maxWidth: 520, mx: "auto" }}>
        <Typography
          component="label"
          htmlFor="copy-configuration"
          sx={{
            display: "block",
            color: "#fff",
            fontSize: "0.85rem",
            fontWeight: 600,
            letterSpacing: "0.04em",
            mb: 1,
          }}
        >
          COPY CONFIGRATION
        </Typography>
        <FormControl fullWidth size="small">
          <CssSelectField
            id="copy-configuration"
            multiple
            value={selectedSimulationIds}
            onChange={handleSimulationSelectionChange}
            disabled={isSimulationListLoading || !resolvedCategoryId}
            displayEmpty
            renderValue={(selected) => {
              if (isSimulationListLoading) {
                return "Loading simulations...";
              }
              if (!selected?.length) {
                return "Select simulations";
              }
              const titles = getSelectedTitles(selected);
              if (!titles.length) {
                return `${selected.length} selected`;
              }
              if (titles.length <= 2) {
                return titles.join(", ");
              }
              return `${titles.length} simulations selected`;
            }}
            MenuProps={MENU_PROPS}
            sx={{
              mt: 0,
              "& .MuiSelect-select": {
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                color: selectedSimulationIds.length
                  ? "#fff"
                  : "rgba(255,255,255,0.65)",
              },
            }}
          >
            {!isSimulationListLoading &&
              !simulationListError &&
              categorySimulations.length === 0 && (
                <MenuItem disabled value="__no_simulations__">
                  No simulations found for this category.
                </MenuItem>
              )}
            {!isSimulationListLoading &&
              !simulationListError &&
              categorySimulations.map((simulation) => (
                <MenuItem key={simulation._id} value={simulation._id}>
                  <Checkbox
                    checked={selectedSimulationIds.indexOf(simulation._id) > -1}
                    sx={{ color: "#fff", "&.Mui-checked": { color: "#a084c7" } }}
                  />
                  <ListItemText
                    primary={simulation.title}
                    primaryTypographyProps={{
                      sx: {
                        color: "#fff",
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                      },
                    }}
                  />
                </MenuItem>
              ))}
          </CssSelectField>
        </FormControl>

        {isSimulationListLoading && (
          <Typography sx={{ color: "rgba(255,255,255,0.75)", fontSize: "0.8rem", mt: 1 }}>
            Loading simulations...
          </Typography>
        )}

        {simulationListError && !isSimulationListLoading && (
          <Typography
            sx={{ color: "#f44336", fontSize: "0.8rem", mt: 1 }}
          >
            {simulationListError}
          </Typography>
        )}

        <Stack alignItems="center" sx={{ mt: 2.5 }}>
          <CssButton
            type="button"
            variant="contained"
            onClick={handleCopyConfiguration}
            disabled={isCopyButtonDisabled}
            sx={{
              background: purpleLight,
              color: "#fff",
              width: "fit-content",
              minWidth: 180,
              "&:hover": { background: purpleLight },
            }}
          >
            {isCopyingConfiguration ? (
              <CssCircularProgress size={20} />
            ) : (
              "Copy Configuration"
            )}
          </CssButton>
        </Stack>
      </Box>

      <CustomSnackbar
        open={openSnackbar}
        message={snackbarMessage}
        severity={snackbarSeverity}
        handleClose={() => setOpenSnackbar(false)}
      />
    </Container>
  );
};

export default ThemeConfigurationForm;
